import {
  Box,
  CircularProgress,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { HubCard } from "~/components/hub-card";
import { Link } from "react-router-dom";
import { ScopeType, SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useTopActionsCard } from "./hooks/useTopActionsCard";
import { PolicyIcon } from "~/icons";
import { DataTable } from "~/components/data-table";
import TopActionsCardEffortCell from "~/components/FirewatchRow/TopActionsCardEffortCell";
import { TopActionsAssetsCountCell } from "~/components/FirewatchRow/TopActionsAssetsCountCell";
import { ActionCell } from "~/components/FirewatchRow/ActionCell";
import PaginationContained from "~/components/PaginationContained/PaginationContained";
import { ClampWithTooltip } from "~/components/ClampWithTooltip/ClampWithTooltip";
import { AggregateScoreType, OrderDirection } from "~/operations";
import { DynamicIcon } from "~/components/icons/DynamicIcon";
import { getLinkByType } from "~/components/FirewatchPage/lib";
import { formatDistanceToNowStrict } from "date-fns";
import { Flex } from "~/components/Flex";
import { TopActionsCardSkeleton } from "~/pages/space/home-dashboard/TopActionsCard/components/TopActionsCardSkeleton";
import TopActionsCardEmptyState from "~/pages/space/home-dashboard/TopActionsCard/components/TopActionsCardEmptyState";

interface TopActionsCardProps {
  scope: SpaceOrWorkspaceScope;
  spaceScope: SpaceScope;
  lastRefreshed?: string;
  isRefreshing?: boolean;
}

export function TopActionsCard({
  scope,
  spaceScope,
  lastRefreshed,
  isRefreshing,
}: TopActionsCardProps) {
  const {
    findings,
    totalFindings,
    loading,
    tableHeaders,
    sortField,
    sortDirection,
    handleSortChange,
    page,
    handlePageChange,
    totalPages,
  } = useTopActionsCard(scope);

  const isSpaceDashboard = scope.type === ScopeType.Space;
  const hasNoFindings = findings.length === 0;
  const isEmpty = hasNoFindings && !loading;
  const isLoading = loading;

  const tagline =
    (hasNoFindings && !isLoading) || !isSpaceDashboard ? (
      <></>
    ) : (
      <Flex
        sx={{
          color: "text.secondary",
          alignItems: "center",
          gap: 0.5,
          mt: 0.8,
          textTransform: "initial",
        }}
      >
        {isRefreshing && (
          <CircularProgress size={13} sx={{ color: "text.secondary" }} />
        )}

        <Typography variant="caption">
          <strong>{isRefreshing ? "Updating ..." : "Last Updated:"}</strong>{" "}
          {!isRefreshing && (
            <>
              {lastRefreshed
                ? `${formatDistanceToNowStrict(lastRefreshed)} ago`
                : "Never"}
            </>
          )}
        </Typography>
      </Flex>
    );

  const tooltip = (
    <Typography variant="caption">
      This list prioritizes actions that will provide the greatest risk
      reduction.
      <ul style={{ paddingLeft: "16px" }}>
        <li>
          <strong>Ranking:</strong> Based on vulnerability severity and the
          number of affected assets.
        </li>
        <li>
          <strong>Scope:</strong> Only includes findings with a defined
          remediation path.
        </li>
      </ul>
    </Typography>
  );

  const title = (
    <Flex justifyContent="space-between" alignItems="flex-start">
      <Typography
        variant="h5"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontWeight: 700,
          mb: isSpaceDashboard ? 0 : "26px",
          fontSize: { xs: 20, md: 24 },
        }}
      >
        TOP ACTIONS FOR RISK REDUCTION
        <Tooltip title={tooltip} arrow placement="right">
          <InfoOutlinedIcon sx={{ color: "secondary.main", width: 16 }} />
        </Tooltip>
      </Typography>
    </Flex>
  );

  return (
    <HubCard
      loading={false}
      title={title}
      tagline={tagline}
      icon={<PolicyIcon />}
      data-testid="top-actions-card"
      sx={{
        minHeight: 376,
      }}
    >
      {!isLoading && !isEmpty && (
        <Stack
          justifyContent="space-between"
          flexDirection="column"
          height="100%"
        >
          <DataTable firewatchSize="small">
            <TableHead
              sx={{
                backgroundColor: "inherit",
                "& th": {
                  fontSize: 10,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  borderBottom: "none",
                  pl: 0,
                  pb: "0 !important",
                },
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <TableRow>
                {tableHeaders.map((header) => {
                  const { label, options, id } = header;

                  return (
                    <TableCell
                      key={id}
                      align={options?.textAlign}
                      width={options?.width}
                      sx={{
                        fontWeight: "500 !important",
                        textWrap: "nowrap",
                        ...options?.sx,
                      }}
                    >
                      {header.sortField ? (
                        <TableSortLabel
                          active={sortField === header.sortField}
                          sx={{
                            "& .MuiTableSortLabel-icon": { fontSize: 10 },
                          }}
                          onClick={() =>
                            handleSortChange(
                              header.sortField!,
                              sortField === header.sortField
                                ? sortDirection === OrderDirection.Asc
                                  ? OrderDirection.Desc
                                  : OrderDirection.Asc
                                : sortDirection,
                            )
                          }
                          direction={
                            sortDirection === OrderDirection.Asc
                              ? "asc"
                              : "desc"
                          }
                        >
                          {label}
                        </TableSortLabel>
                      ) : (
                        label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {findings.map((f) => {
                const url = getLinkByType({
                  type: "TopActionsRiskReduction",
                  findingMrn: f.findingMrn,
                  scoreType: f.scoreType,
                  entity:
                    f.entity?.__typename === "EntityInfoAsset"
                      ? f?.entity
                      : undefined,
                  queryParams: scope.params,
                });

                return (
                  <TableRow
                    key={f.findingMrn}
                    sx={{ td: { pl: 0 } }}
                    data-testid="top-action-risk-table-row"
                  >
                    {/* <TopActionsCardSpaceRiskCell
                      spaceRiskImprovement={f.spaceRiskImprovement || 0}
                    /> */}

                    <TableCell>
                      <Box
                        component={Link}
                        to={url}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {f.scoreType !== AggregateScoreType.Check && (
                          <DynamicIcon iconId={f.iconId} size={18} />
                        )}
                        <ClampWithTooltip>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 400,
                              lineHeight: "16px",
                              color: "text.primary",
                            }}
                          >
                            {f.title}
                          </Typography>
                        </ClampWithTooltip>
                      </Box>
                    </TableCell>

                    <TopActionsAssetsCountCell
                      isSmallTable={false}
                      count={f.blastRadius?.affected || 0}
                    />

                    <TopActionsCardEffortCell
                      effort={f.effort || 0}
                      manualEffort={f.manualEffort}
                    />

                    <ActionCell row={f} />
                  </TableRow>
                );
              })}
            </TableBody>
          </DataTable>

          <PaginationContained
            itemCount={totalFindings}
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            itemType="finding"
          />
        </Stack>
      )}

      {isLoading && <TopActionsCardSkeleton />}

      {isEmpty && (
        <TopActionsCardEmptyState
          space={spaceScope}
          totalFindings={totalFindings}
          isSpaceDashboard={isSpaceDashboard}
        />
      )}
    </HubCard>
  );
}
